import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { ConfirmationDialog, ManageCompanyEvent, Switch } from "klayowebshared";
import { Component } from "react";
import { AppContext } from "../../common/AppContext";
import axiosClient from "../../common/AxiosClient";
import { track } from "../../common/track";
import { ErrorModel } from "../../common/models/ErrorModel";
import { Button } from "../../components/Button";
import { CompanyIntegrationCard } from "../../components/CompanyIntegrationCard";
import { Paper } from "../../components/Paper";
import { Snackbar } from "../../components/Snackbar";
import { AddressTable } from "../../components/table/AddressTable";
import { LocationTable } from "../../components/table/LocationTable";
import { TooltipIcon } from "../../components/TooltipIcon";
import { CREATE_PROVIDER_MODE } from "../../constants";
import { Organization } from "../../data/Organization";
import {
  CreateProviderRequest,
  Provider,
  UpdateProviderRequest
} from "../../interfaces/ProviderTraining";
import { withLocale } from "../../providers/LocaleProvider";
import AddIcon from "../../resources/images/icons-add.svg";
import EditIcon from "../../resources/images/icons-edit.svg";
import { AddEditAddressDialog } from "./AddEditAddressDialog";
import { AddEditLocationDialog } from "./AddEditLocationDialog";
import { CompanyNameDialog } from "./CompanyNameDialog";
import CreateProviderDialog from "./CreateProviderDialog";

class SettingsCompanyViewBase extends Component<any, any> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static menuType = {
    address: 0,
    location: 1,
    integration: 2
  };

  constructor(props: any) {
    super(props);

    this.state = {
      addLocationError: null,
      deleteLocationError: null,
      deleteAddressError: null,
      savingLocation: false,
      actionTarget: null,
      actionLocation: null,
      actionProvider: null,
      menuType: null,
      deletingLocation: null,
      deletingProvider: null,
      deletingAddress: null,
      hasResetDemoData: false,
      successMessage: null,
      enableJobMatching: false,
      allowJobMatchingConfig: false,
      showCreateProviderDialog: false,
      providerDialogMode: CREATE_PROVIDER_MODE.CREATE,
      editingProvider: null,
      savingProvider: false,
      addProviderError: null,
      deleteProviderError: null,
      showProviderTrainingWarning: false
    };
  }
  componentDidMount() {
    track(ManageCompanyEvent.Viewed);
    this.setState({
      enableJobMatching: this.props.orgSettings?.enableJobMatching ?? false,
      allowJobMatchingConfig: this.props.orgSettings?.allowJobMatchingConfig ?? false
    });
  }
  //  Add provider
  onShowCreateTaskDialog() {
    this.setState({
      showCreateProviderDialog: true,
      providerDialogMode: CREATE_PROVIDER_MODE.CREATE,
      editingProvider: null
    });
  }
  onShowEditProviderDialog(provider: any) {
    this.setState({
      showCreateProviderDialog: true,
      providerDialogMode: CREATE_PROVIDER_MODE.EDIT,
      editingProvider: provider
    });
  }
  onCloseCreateTaskDialog() {
    this.setState({
      showCreateProviderDialog: false,
      editingProvider: null,
      addProviderError: null
    });
  }
  onCreateProvider(longName: string, shortName: string) {
    const { orgSettings } = this.props;

    this.setState({ savingProvider: true });

    axiosClient
      .post<CreateProviderRequest>("/Integration", {
        name: longName,
        abbreviation: shortName
      })
      .then((response) => {
        // Add the new provider to orgSettings
        if (orgSettings && orgSettings.organizationIntegrations && response.data) {
          const newProvider = response.data.integration;
          orgSettings.addProvider(newProvider);
        }

        track(ManageCompanyEvent.TrainingProviderAdded);
        this.setState({
          showCreateProviderDialog: false,
          successMessage: "Provider added successfully"
        });
      })
      .catch((e) => {
        this.setState({
          addProviderError: ErrorModel.parseServerError(e)
        });
      })
      .finally(() => {
        this.setState({ savingProvider: false });
      });
  }

  onUpdateProvider(longName: string, shortName: string, providerId: string) {
    const { orgSettings } = this.props;

    this.setState({ savingProvider: true });

    return axiosClient
      .put<UpdateProviderRequest>("/Integration", {
        id: providerId,
        name: longName,
        abbreviation: shortName
      })
      .then((response) => {
        // Update the provider in orgSettings
        if (orgSettings && orgSettings.organizationIntegrations) {
          const updatedProvider = {
            id: response.data.id,
            name: response.data.name,
            abbreviation: response.data.abbreviation,
            integrationSource: response.data.integrationSource
          };

          // Update the provider in the state
          orgSettings.updateProvider(updatedProvider);
        }

        this.setState({
          showCreateProviderDialog: false,
          editingProvider: null,
          successMessage: "Provider updated successfully"
        });
      })
      .catch((e) => {
        this.setState({
          addProviderError: ErrorModel.parseServerError(e)
        });
      })
      .finally(() => {
        this.setState({ savingProvider: false });
      });
  }
  onSaveProvider(longName: string, shortName: string, providerId = null) {
    if (providerId) {
      return this.onUpdateProvider(longName, shortName, providerId);
    }
    return this.onCreateProvider(longName, shortName);
  }
  onIntegrationAction(e: any, integration: Provider) {
    this.setState({
      actionProvider: integration
    });
  }
  onEditIntegration() {
    const { actionProvider } = this.state;
    track(ManageCompanyEvent.TrainingProviderMenuOption, { option: "edit" });
    this.onCloseMenu();

    // Show the edit dialog
    this.onShowEditProviderDialog(actionProvider);
  }
  onDeleteIntegration() {
    const { actionProvider } = this.state;
    track(ManageCompanyEvent.TrainingProviderMenuOption, { option: "delete" });
    this.onCloseMenu();

    if (actionProvider.hasTraining) {
      this.setState({
        deletingProvider: actionProvider,
        showProviderTrainingWarning: true
      });
    } else {
      this.deleteProvider(actionProvider);
    }
  }
  onDeleteProviderConfirmed() {
    const { deletingProvider } = this.state;
    this.deleteProvider(deletingProvider);
  }
  onCancelDeleteProvider() {
    this.setState({
      deletingProvider: null,
      deleteProviderError: null,
      showProviderTrainingWarning: false
    });
  }
  deleteProvider(provider: any) {
    const { orgSettings } = this.props;

    axiosClient
      .delete(`/Integration/${provider.id}`)
      .then((response: any) => {
        // Remove the provider from orgSettings
        if (orgSettings && orgSettings.organizationIntegrations) {
          orgSettings.deleteProvider(provider);
        }

        this.setState({
          deletingProvider: null,
          deleteProviderError: null,
          showProviderTrainingWarning: false,
          successMessage: "Provider deleted successfully"
        });
        this.forceUpdate();
      })
      .catch((e: any) => {
        this.setState({
          deletingProvider: provider,
          deleteProviderError: ErrorModel.parseServerError(e)
        });
      })
      .finally(() => {});
  }
  // New method to handle job matching toggle
  onToggleJobMatching = async (enabled: boolean) => {
    try {
      track(ManageCompanyEvent.JobMatchingToggle, { state: enabled ? "on" : "off" });
      this.setState({
        enableJobMatching: enabled
      });

      this.context.setLoading("jobMatching", true);

      await axiosClient.put("/Organization/Admin/Settings", {
        organizationId: this.props.organization.id,
        enableJobMatching: enabled
      });

      this.context.organization && (this.context.organization.enableJobMatching = enabled);

      this.props.onLoadOrgSettings(false);

      this.setState({
        successMessage: `Job matching ${enabled ? "enabled" : "disabled"} successfully`
      });
    } catch (e) {
      this.setState({
        successMessage: null,
        error: ErrorModel.parseServerError(e)
      });
      this.setState({
        enableJobMatching: !enabled
      });
    } finally {
      this.context.setLoading("jobMatching", false);
    }
  };

  componentDidUpdate(prevProps: any, prevState: any) {
    // window.scroll({ top: 0, left: 0, behavior: "smooth" });
    const prevMessage = prevProps?.location?.state?.successMessage;
    const currentMessage = this.props?.location?.state?.successMessage;
    if (prevMessage !== currentMessage && !!currentMessage) {
      this.setState({ successMessage: currentMessage });
    }

    if (
      prevProps.orgSettings?.allowJobMatchingConfig !==
      this.props.orgSettings?.allowJobMatchingConfig
    ) {
      this.setState({
        allowJobMatchingConfig: this.props.orgSettings?.allowJobMatchingConfig
      });
    }
    if (prevProps.orgSettings?.enableJobMatching !== this.props.orgSettings?.enableJobMatching) {
      this.setState({
        enableJobMatching: this.props.orgSettings?.enableJobMatching
      });
    }
  }

  onCloseCompanyNameDialog() {
    this.props.history.push("/settings/company");
  }

  onCloseAddEditLocationDialog() {
    this.props.history.push("/settings/company");
    this.setState({ addLocationError: null });
  }

  onCloseAddEditAddressDialog() {
    this.props.history.push("/settings/company");
    this.setState({ addAddressError: null });
  }

  onCompanyNameChange(name: any) {
    const { organization, onCompanyNameChange } = this.props;
    organization.name = name;
    this.props.history.push("/settings/company");
    if (onCompanyNameChange) onCompanyNameChange(name);
  }

  onResetDemoData() {
    const { user } = this.state;

    this.context.setLoading("resetDemoData", true);

    axiosClient
      .put("/Organization/ResetDemo", null, {
        withCredentials: true
      })
      .then((response: any) => {
        this.setState({ hasResetDemoData: true });
        window.location.reload();
      })
      .finally(() => {
        this.context.setLoading("resetDemoData", false);
      });
  }

  onLocationAction(e: any, location: any) {
    this.setState({
      actionTarget: e.target,
      actionLocation: location,
      menuType: SettingsCompanyViewBase.menuType.location
    });
  }

  onAddressAction(e: any, location: any) {
    this.setState({
      actionTarget: e.target,
      actionLocation: location,
      menuType: SettingsCompanyViewBase.menuType.address
    });
  }

  onSaveLocation(locationId: any, name: any) {
    const { orgSettings } = this.props;

    this.context.setLoading("saving", true);
    this.setState({ savingLocation: true });

    if (locationId) {
      axiosClient
        .put("/Organization/Admin/Location", {
          locationId,
          name
        })
        .then((response: any) => {
          orgSettings.updateLocation(response.data.location);
          this.onCloseAddEditLocationDialog();
        })
        .catch((e: any) => {
          this.setState({ addLocationError: ErrorModel.parseServerError(e) });
        })
        .finally(() => {
          this.context.setLoading("saving", false);
          this.setState({ savingLocation: false });
        });
    } else {
      axiosClient
        .post("/Organization/Admin/Location", {
          name: name
        })
        .then((response: any) => {
          orgSettings.addLocation(response.data.location);
          this.onCloseAddEditLocationDialog();
        })
        .catch((e: any) => {
          this.setState({ addLocationError: ErrorModel.parseServerError(e) });
        })
        .finally(() => {
          this.context.setLoading("saving", false);
          this.setState({ savingLocation: false });
        });
    }
  }

  onEditAddress() {
    this.onCloseMenu();

    this.props.history.push("/settings/company/address/edit/");
  }

  deleteAddress() {
    const { orgSettings } = this.props;
    this.context.setLoading("deleting", true);

    axiosClient
      .delete("/Organization/Admin/Address", {
        data: {},
        withCredentials: true
      })
      .then((response: any) => {
        orgSettings.deleteAddress();
        this.setState({
          deletingAddress: null,
          deleteAddressError: null,
          successMessage: "Delete address successfully"
        });
        this.forceUpdate();
      })
      .catch((e: any) => {
        this.setState({
          deletingAddress: true,
          deleteAddressError: ErrorModel.parseServerError(e)
        });
      })
      .finally(() => {
        this.context.setLoading("deleting", false);
      });
  }

  onDeleteAddress() {
    this.setState({ deletingAddress: true });
    this.onCloseMenu();
  }

  onDeleteAddressConfirmed() {
    this.deleteAddress();
    this.setState({ deletingAddress: null });
  }

  onCancelDeleteAddress() {
    this.setState({ deletingAddress: null, deleteAddressError: null });
  }

  onEditLocation() {
    const { actionLocation } = this.state;
    track(ManageCompanyEvent.LocationMenuOption, { option: "edit" });
    this.onCloseMenu();

    this.props.history.push("/settings/company/locations/edit/" + actionLocation.locationId);
  }

  onDeleteLocation() {
    const { actionLocation } = this.state;
    track(ManageCompanyEvent.LocationMenuOption, { option: "delete" });

    if (actionLocation.isInUsed) this.setState({ deletingLocation: actionLocation });
    else this.deleteLocation(actionLocation);

    this.onCloseMenu();
  }

  onDeleteLocationConfirmed() {
    const { deletingLocation } = this.state;
    this.deleteLocation(deletingLocation);
    // this.setState({ deletingLocation: null });
  }

  onCancelDeleteLocation() {
    this.setState({ deletingLocation: null, deleteLocationError: null });
  }

  deleteLocation(location: any) {
    const { orgSettings } = this.props;
    this.context.setLoading("deleting", true);

    axiosClient
      .delete("/Organization/Admin/Location", {
        data: { locationId: location.locationId }
      })
      .then((response: any) => {
        orgSettings.deleteLocation(location);
        this.setState({ deletingLocation: null, deleteLocationError: null });
        this.forceUpdate();
      })
      .catch((e: any) => {
        this.setState({
          deletingLocation: location,
          deleteLocationError: ErrorModel.parseServerError(e)
        });
      })
      .finally(() => {
        this.context.setLoading("deleting", false);
      });
  }

  onCloseMenu() {
    this.setState({ actionTarget: null, actionLocation: null, menuType: null });
  }

  onSnackBarClose() {
    this.setState({ successMessage: null });
  }

  render() {
    const { organization, orgSettings, theme, user, location } = this.props;
    const {
      menuType,
      savingLocation,
      addLocationError,
      actionTarget,
      deletingAddress,
      deletingLocation,
      hasResetDemoData,
      deleteLocationError,
      deleteAddressError,
      successMessage,
      enableJobMatching,
      allowJobMatchingConfig,
      showCreateProviderDialog,
      providerDialogMode,
      editingProvider,
      showProviderTrainingWarning
    } = this.state;
    const path = location.pathname;
    const showCompanyNameDialog = path.startsWith("/settings/company/edit");
    const editingLocation = path.startsWith("/settings/company/locations/edit/");
    const showAddEditLocationDialog =
      path.startsWith("/settings/company/locations/new") || editingLocation;
    const editingAddress = path.startsWith("/settings/company/address/edit/");
    const showAddEditAddressDialog =
      path.startsWith("/settings/company/address/new") || editingAddress;

    let editLocation = null;

    if (orgSettings && editingLocation) {
      const editLocationId = path.replace("/settings/company/locations/edit/", "");
      editLocation = orgSettings.getLocation(editLocationId);
    }

    return (
      <div>
        <Paper
          theme={theme}
          padding={{ xs: "40px 24px", md: "56px 64px" }}
          borderFromBreakpoint='md'
        >
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
            <h1 style={{ margin: 0 }}>{orgSettings ? orgSettings.name : <Skeleton />}</h1>
            <Button
              path={"/settings/company/edit"}
              disabled={false}
              icon={<EditIcon width='20px' height='20px' />}
            />
          </Stack>

          <Divider sx={{ margin: "40px 0" }} />
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
            <div>
              <h2>{(this.props as any).translate("settingsViews.company.address")}</h2>
              <div className='klayo-instruction'>
                {(this.props as any).translate("settingsViews.company.manageAddress")}
              </div>
            </div>
            {orgSettings && !orgSettings.address && (
              <Button
                path={"/settings/company/address/new"}
                size='md'
                theme={theme}
                startIcon={<AddIcon className='klayo-add-icon' />}
                showLabelFromBreakpoint='md'
                label={(this.props as any).translate("settingsViews.company.addAddress")}
                onClick={() => track(ManageCompanyEvent.AddAddress)}
              />
            )}
          </Stack>
          {orgSettings && orgSettings.address && (
            <AddressTable
              sx={{ marginTop: "32px" }}
              theme={theme}
              showHeader={false}
              pagination={false}
              showFooter={false}
              minHeight={
                orgSettings && orgSettings.address && orgSettings.address.length !== 0
                  ? ""
                  : "300px"
              }
              dense={true}
              onAddressAction={this.onAddressAction.bind(this)}
              paper={false}
              rows={orgSettings && orgSettings.address ? [orgSettings.address] : null}
            />
          )}

          <Divider sx={{ margin: "40px 0" }} />
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
            <div>
              <h2>{(this.props as any).translate("settingsViews.company.locations")}</h2>
              <div className='klayo-instruction'>
                {(this.props as any).translate("settingsViews.company.manageLocations")}
              </div>
            </div>
            <Button
              path={"/settings/company/locations/new"}
              size='md'
              theme={theme}
              startIcon={<AddIcon className='klayo-add-icon' />}
              showLabelFromBreakpoint='md'
              label={(this.props as any).translate("settingsViews.company.addLocation")}
              onClick={() => track(ManageCompanyEvent.LocationAdd)}
            />
          </Stack>
          {orgSettings && orgSettings.locations && orgSettings.locations.length !== 0 && (
            <LocationTable
              sx={{ marginTop: "32px" }}
              theme={theme}
              showHeader={false}
              pagination={false}
              showFooter={false}
              minHeight={
                orgSettings && orgSettings.locations && orgSettings.locations.length !== 0
                  ? ""
                  : "300px"
              }
              dense={true}
              onLocationAction={this.onLocationAction.bind(this)}
              paper={false}
              rows={orgSettings ? orgSettings.locations : null}
            />
          )}

          {actionTarget && (
            <Menu
              open={true}
              anchorEl={actionTarget}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              onClose={this.onCloseMenu.bind(this)}
            >
              {menuType === SettingsCompanyViewBase.menuType.location ? (
                <>
                  <MenuItem onClick={this.onEditLocation.bind(this)}>
                    <ListItemText>
                      {(this.props as any).translate("settingsViews.company.editLocation")}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={this.onDeleteLocation.bind(this)}>
                    <ListItemText>
                      {(this.props as any).translate("settingsViews.company.deleteLocation")}
                    </ListItemText>
                  </MenuItem>
                </>
              ) : menuType === SettingsCompanyViewBase.menuType.address ? (
                <>
                  <MenuItem onClick={this.onEditAddress.bind(this)}>
                    <ListItemText>
                      {(this.props as any).translate("settingsViews.company.editAddress")}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={this.onDeleteAddress.bind(this)}>
                    <ListItemText>
                      {(this.props as any).translate("settingsViews.company.deleteAddress")}
                    </ListItemText>
                  </MenuItem>
                </>
              ) : null}
            </Menu>
          )}

          <Divider sx={{ margin: "40px 0" }} />
          <div style={{ marginBottom: "40px" }}>
            <h2>{(this.props as any).translate("settingsViews.company.proficiency")}</h2>
            <div className='klayo-instruction'>
              {(this.props as any).translate("settingsViews.company.proficiencyDescription")}
            </div>
          </div>
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
            <Switch
              label={
                (this.props as any).translate("settingsViews.company.proficiency") +
                " " +
                (orgSettings && orgSettings.showProficiency
                  ? (this.props as any).translate("common.enabled")
                  : (this.props as any).translate("common.disabled"))
              }
              checked={orgSettings?.showProficiency ?? false}
              disabled={true}
            />
            <TooltipIcon
              onOpen={() => track(ManageCompanyEvent.ProficiencyInfo)}
              text={
                (this.props as any).translate("settingsViews.company.contactToDiscussProficiency") +
                (orgSettings && orgSettings.showProficiency
                  ? (this.props as any).translate("common.off")
                  : (this.props as any).translate("common.on"))
              }
            />
          </Stack>

          <Divider sx={{ margin: "40px 0" }} />
          <div style={{ marginBottom: "40px" }}>
            <h2>{(this.props as any).translate("settingsViews.company.jobMatching")}</h2>
            <div className='klayo-instruction'>
              {(this.props as any).translate("settingsViews.company.jobMatchingDescription")}
            </div>
          </div>
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
            <Switch
              label={`${(this.props as any).translate("settingsViews.company.jobMatching")} ${enableJobMatching ? (this.props as any).translate("common.enabled") : (this.props as any).translate("common.disabled")}`}
              disabled={!(allowJobMatchingConfig || enableJobMatching)}
              checked={enableJobMatching}
              onChange={(e: any) => this.onToggleJobMatching(e.target.checked)}
            />
            {!(allowJobMatchingConfig || enableJobMatching) && (
              <TooltipIcon
                text={(this.props as any).translate(
                  "settingsViews.company.contactToDiscussJobMatching"
                )}
              />
            )}
          </Stack>

          {!!organization?.enabledFeatures?.Training && (
            <div>
              <Divider sx={{ margin: "40px 0" }} />
              <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                <div>
                  <h2>
                    {(this.props as any).translate("settingsViews.company.trainingProviders")}
                  </h2>
                  <div className='klayo-instruction'>
                    {(this.props as any).translate(
                      "settingsViews.company.trainingProvidersDescription"
                    )}
                  </div>
                </div>
                <Button
                  size='md'
                  theme={theme}
                  startIcon={<AddIcon className='klayo-add-icon' />}
                  showLabelFromBreakpoint='md'
                  label={(this.props as any).translate("settingsViews.company.addProvider")}
                  onClick={this.onShowCreateTaskDialog.bind(this)}
                />
              </Stack>
              {orgSettings &&
                orgSettings.organizationIntegrations &&
                orgSettings.organizationIntegrations.length !== 0 && (
                  <Stack direction='column' justifyContent='space-between' alignItems='center'>
                    {orgSettings.organizationIntegrations.map((integrationItem: any) => (
                      <CompanyIntegrationCard
                        key={integrationItem.id}
                        organization={organization ? organization : null}
                        organizationIntegration={integrationItem}
                        onIntegrationAction={this.onIntegrationAction.bind(this)}
                        onEditIntegration={this.onEditIntegration.bind(this)}
                        onDeleteIntegration={this.onDeleteIntegration.bind(this)}
                        translate={(this.props as any).translate}
                      />
                    ))}
                  </Stack>
                )}
            </div>
          )}

          {organization && organization.isDemo && (
            <div>
              <Divider sx={{ margin: "40px 0" }} />
              <div>
                <h2>{(this.props as any).translate("settingsViews.company.resetDemoData")}</h2>
                <div className='klayo-instruction'>
                  {(this.props as any).translate("settingsViews.company.resetDemoDataDescription")}
                </div>
              </div>
              <Button
                label={(this.props as any).translate("settingsViews.company.resetData")}
                variant='filled'
                disabled={
                  hasResetDemoData || organization.demoOrgStatus === Organization.status.resetting
                }
                onClick={this.onResetDemoData.bind(this)}
              />
            </div>
          )}
        </Paper>

        {showCompanyNameDialog && (
          <CompanyNameDialog
            organization={organization}
            theme={theme}
            onSave={this.onCompanyNameChange.bind(this)}
            onClose={this.onCloseCompanyNameDialog.bind(this)}
          />
        )}

        {showAddEditLocationDialog && (
          <AddEditLocationDialog
            theme={theme}
            saving={savingLocation}
            location={editLocation}
            onSave={this.onSaveLocation.bind(this)}
            error={addLocationError}
            onClose={this.onCloseAddEditLocationDialog.bind(this)}
          />
        )}

        {showAddEditAddressDialog && (
          <AddEditAddressDialog
            theme={theme}
            saving={savingLocation}
            isEdit={editingAddress}
            orgSettings={orgSettings}
            error={addLocationError}
            onClose={this.onCloseAddEditAddressDialog.bind(this)}
            address={orgSettings?.address ? orgSettings?.address : null}
            {...this.props}
          />
        )}

        {deletingLocation && (
          <ConfirmationDialog
            theme={theme}
            title={(this.props as any).translate("settingsViews.company.deleteLocationTitle")}
            question={(this.props as any).translate("settingsViews.company.deleteLocationConfirm")}
            cancelButton={(this.props as any).translate("common.no")}
            acceptButton={(this.props as any).translate("common.yes")}
            acceptDanger={true}
            error={deleteLocationError}
            onCancel={this.onCancelDeleteLocation.bind(this)}
            onAccept={this.onDeleteLocationConfirmed.bind(this)}
          />
        )}

        {deletingAddress && (
          <ConfirmationDialog
            theme={theme}
            title={(this.props as any).translate("settingsViews.company.deleteAddressTitle")}
            question={(this.props as any).translate("settingsViews.company.deleteAddressConfirm")}
            cancelButton={(this.props as any).translate("common.cancel")}
            acceptButton={(this.props as any).translate("common.delete")}
            acceptDanger={true}
            error={deleteAddressError}
            onCancel={this.onCancelDeleteAddress.bind(this)}
            onAccept={this.onDeleteAddressConfirmed.bind(this)}
          />
        )}
        {showProviderTrainingWarning && (
          <ConfirmationDialog
            theme={theme}
            title={(this.props as any).translate("settingsViews.company.deleteProviderTitle")}
            question={(this.props as any).translate("settingsViews.company.deleteProviderWarning")}
            cancelButton={(this.props as any).translate("common.close")}
            isNotShowAcceptButton={true}
            onCancel={this.onCancelDeleteProvider.bind(this)}
            // onAccept={this.onDeleteProviderConfirmed.bind(this)}
          />
        )}
        {showCreateProviderDialog && (
          <CreateProviderDialog
            theme={theme}
            onClose={this.onCloseCreateTaskDialog.bind(this)}
            onSave={this.onSaveProvider.bind(this)}
            error={this.state.error}
            mode={this.state.providerDialogMode}
            providerData={this.state.editingProvider}
            isLoading={this.state.savingProvider}
          />
        )}
        {successMessage && (
          <Snackbar
            open={true}
            duration={3000}
            type='success'
            onClose={this.onSnackBarClose.bind(this)}
            message={successMessage}
          />
        )}
      </div>
    );
  }
}

export const SettingsCompanyView = withLocale(SettingsCompanyViewBase);
